// Phase 2 — Payouts orchestrator. Owns state + data fetching + handlers
// shared across the three tab views. The actual tab UIs live under
// `./payouts/`. Net Payable breakdown modal is also extracted.
//
// Same workflow, same UI, same data flow as the prior 1,084-line monolith
// — just split into focused files for maintainability.

import { useEffect, useMemo, useState } from "react";
import { api, apiUrl, fmtINR, fmtPeriod, periodOptions } from "@/lib/api";
import { PageHeader, Btn, Select, Modal, Input } from "@/components/Shared";
import { Download, Lock } from "lucide-react";
import { toast } from "sonner";
import { openConfirm, openPrompt } from "@/lib/dialogs";

import PayoutsByPartnerTab from "./payouts/PayoutsByPartnerTab";
import PayoutsByMerchantTab from "./payouts/PayoutsByMerchantTab";
import PayoutsHistoryTab, { EMPTY_HIST } from "./payouts/PayoutsHistoryTab";
import PayoutBreakdownModal from "./payouts/PayoutBreakdownModal";
import { makeShareFormatter } from "./payouts/shareFormatter";

export default function Payouts() {
  const [tab, setTab] = useState("partner"); // "partner" | "merchant" | "history"
  const [data, setData] = useState({ summary: [], by_period: [], available_periods: [] });
  const [calcRows, setCalcRows] = useState([]);
  const [period, setPeriod] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [gatewayFilter, setGatewayFilter] = useState("");
  const [lobFilter, setLobFilter] = useState("");
  const [partnerTabFilter, setPartnerTabFilter] = useState("");
  // Adjustments state (By Partner tab)
  const [adjustments, setAdjustments] = useState([]);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ partner_id: "", period: "", label: "", amount: 0, notes: "" });
  const [adjEditId, setAdjEditId] = useState(null);
  // History sub-tab state
  const [history, setHistory] = useState([]);
  const [partners, setPartners] = useState([]);
  const [histPartner, setHistPartner] = useState("");
  const [histFy, setHistFy] = useState("");
  const [histOpen, setHistOpen] = useState(false);
  const [histForm, setHistForm] = useState(EMPTY_HIST);
  const [histEditId, setHistEditId] = useState(null);
  // Breakdown modal
  const [breakdown, setBreakdown] = useState(null);
  // Expanded merchant rows (effective-date detail) — set of expandKey strings
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  // History bulk + sort
  const [histSort, setHistSort] = useState({ key: "period_month", dir: "desc" });
  const [histSelectedIds, setHistSelectedIds] = useState(new Set());

  // ---------- data fetchers ----------
  const load = async (p = period) => {
    const r = await api.get(`/partner-payouts${p ? `?period=${p}` : ""}`);
    setData(r.data);
    setAdjustments(r.data?.adjustments || []);
  };
  const loadCalcs = async (p = period) => {
    const r = await api.get(`/calculations${p ? `?period=${p}` : ""}`);
    setCalcRows(r.data || []);
  };
  const loadHistory = async () => {
    const [h, p] = await Promise.all([
      api.get("/partner-payment-history"),
      api.get("/partners"),
    ]);
    setHistory(h.data || []);
    setPartners((p.data || []).filter((x) => x.partner_id !== "P000"));
  };

  useEffect(() => { load(""); loadCalcs(""); loadHistory(); /* eslint-disable-next-line */ }, []);
  const onPeriod = (v) => { setPeriod(v); load(v); loadCalcs(v); };

  // ---------- derived state ----------
  const partnerById = useMemo(() => Object.fromEntries(partners.map((p) => [p.partner_id, p])), [partners]);
  const partnerLookup = useMemo(() => {
    const m = {};
    (data.summary || []).forEach((s) => { m[s.partner_id] = s.partner_name; });
    (partners || []).forEach((p) => { if (!m[p.partner_id]) m[p.partner_id] = p.partner_name; });
    return m;
  }, [data.summary, partners]);
  const formatShare = useMemo(() => makeShareFormatter(partnerById), [partnerById]);

  const partnerOptions = useMemo(() => {
    const seen = new Map();
    calcRows.forEach((r) => { if (r.referred_partner_id && !seen.has(r.referred_partner_id)) seen.set(r.referred_partner_id, r.referred_partner_name); });
    return [...seen.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([id, name]) => ({ value: id, label: `${id} · ${name}` }));
  }, [calcRows]);
  const gatewayOptions = useMemo(() => [...new Set(calcRows.map((r) => r.gateway).filter(Boolean))].sort(), [calcRows]);
  const lobOptions = useMemo(() => [...new Set(calcRows.map((r) => r.lob).filter(Boolean))].sort(), [calcRows]);

  // Merchant rows — group by (period, merchant, gateway, lob); each merchant×
  // month gets ONE summary row + an `effective_date_lines` sub-array for the
  // expandable detail view. See iter31.5 / iter31.6 for full rationale.
  const merchantRows = useMemo(() => {
    let rows = calcRows.filter((r) => !r.error);
    if (gatewayFilter) rows = rows.filter((r) => r.gateway === gatewayFilter);
    if (lobFilter) rows = rows.filter((r) => r.lob === lobFilter);
    const groups = new Map();
    for (const r of rows) {
      const key = `${r.period}|${r.merchant_id}|${r.gateway}|${r.lob}`;
      if (!groups.has(key)) {
        groups.set(key, {
          period: r.period, merchant_id: r.merchant_id, merchant_name: r.merchant_name,
          gateway: r.gateway, lob: r.lob, processed_volume: 0,
          primary_partner_id: "", primary_partner_name: "", primary_share_pct: 0, primary_share_value: 0, primary_payout: 0,
          secondary_partner_id: "", secondary_partner_name: "", secondary_share_pct: 0, secondary_share_value: 0, secondary_payout: 0,
          // iter39.3 — Full array of co-partner contributions. Each entry:
          // {partner_id, partner_name, share_pct, share_value, payout}. The
          // legacy secondary_* scalars mirror the FIRST entry for backward
          // compatibility with existing UI columns/filters.
          co_partners: [],
          effective_date_lines: new Map(),
        });
      }
      const g = groups.get(key);
      const eff = r.effective_date || "";
      if (!g.effective_date_lines.has(eff)) {
        g.effective_date_lines.set(eff, {
          effective_date: eff, rate_anchor_date: r.rate_anchor_date || "", processed_volume: 0,
          primary_partner_id: "", primary_partner_name: "", primary_share_pct: 0, primary_share_value: 0, primary_payout: 0,
          secondary_partner_id: "", secondary_partner_name: "", secondary_share_pct: 0, secondary_share_value: 0, secondary_payout: 0,
          co_partners: [],
        });
      }
      const sub = g.effective_date_lines.get(eff);
      if (r.partner_tier === "secondary") {
        // Accumulate every secondary calc row into the co_partners array.
        // Multiple entries with the same partner_id (across periods/effective
        // dates) are merged so display shows one row per unique co-partner.
        const upsert = (arr) => {
          const idx = arr.findIndex((c) => c.partner_id === r.referred_partner_id);
          if (idx === -1) {
            arr.push({
              partner_id: r.referred_partner_id,
              partner_name: r.referred_partner_name,
              share_pct: r.partner_share_pct || 0,
              share_value: r.partner_share_value || 0,
              payout: r.partner_payout || 0,
            });
          } else {
            arr[idx].payout += r.partner_payout || 0;
          }
        };
        upsert(g.co_partners);
        upsert(sub.co_partners);
        g.secondary_payout += r.partner_payout || 0;
        sub.secondary_payout += r.partner_payout || 0;
        // Legacy scalars — mirror the FIRST co-partner seen so the existing
        // Payouts By Merchant column keeps rendering meaningfully.
        if (!g.secondary_partner_id) {
          g.secondary_partner_id = r.referred_partner_id;
          g.secondary_partner_name = r.referred_partner_name;
          g.secondary_share_pct = r.partner_share_pct || 0;
          g.secondary_share_value = r.partner_share_value || 0;
        }
        if (!sub.secondary_partner_id) {
          sub.secondary_partner_id = r.referred_partner_id;
          sub.secondary_partner_name = r.referred_partner_name;
          sub.secondary_share_pct = r.partner_share_pct || 0;
          sub.secondary_share_value = r.partner_share_value || 0;
        }
      } else {
        g.primary_partner_id = r.referred_partner_id;
        g.primary_partner_name = r.referred_partner_name;
        if (r.partner_share_pct) g.primary_share_pct = r.partner_share_pct;
        if (r.partner_share_value) g.primary_share_value = r.partner_share_value;
        g.primary_payout += r.partner_payout || 0;
        g.processed_volume += r.processed_volume || 0;
        sub.primary_partner_id = r.referred_partner_id;
        sub.primary_partner_name = r.referred_partner_name;
        sub.primary_share_pct = r.partner_share_pct || 0;
        sub.primary_share_value = r.partner_share_value || 0;
        sub.primary_payout += r.partner_payout || 0;
        sub.processed_volume += r.processed_volume || 0;
      }
    }
    let merged = [...groups.values()].map((g) => ({
      ...g,
      effective_date_lines: [...g.effective_date_lines.values()].sort((a, b) =>
        (a.effective_date || a.rate_anchor_date || "").localeCompare(b.effective_date || b.rate_anchor_date || "")
      ),
    }));
    if (partnerFilter) merged = merged.filter((g) =>
      g.primary_partner_id === partnerFilter
      || (g.co_partners || []).some((c) => c.partner_id === partnerFilter)
    );
    merged.sort((a, b) => (a.period + a.merchant_id).localeCompare(b.period + b.merchant_id));
    return merged;
  }, [calcRows, partnerFilter, gatewayFilter, lobFilter]);

  // Per-partner breakdown source for the Net Payable click-to-explain modal.
  const breakdownData = useMemo(() => {
    if (!breakdown) return null;
    const merchantRowsRaw = calcRows.filter((r) =>
      !r.error && r.referred_partner_id === breakdown.partner_id && (!breakdown.period || r.period === breakdown.period)
    );
    const groups = new Map();
    for (const r of merchantRowsRaw) {
      const key = `${r.period}|${r.merchant_id}`;
      if (!groups.has(key)) {
        groups.set(key, { period: r.period, merchant_id: r.merchant_id, merchant_name: r.merchant_name, gateway: r.gateway, lob: r.lob, tier: r.partner_tier, gross: 0, payout: 0 });
      }
      const g = groups.get(key);
      g.gross += r.gross_commission || 0;
      g.payout += r.partner_payout || 0;
    }
    const merchants = [...groups.values()].sort((a, b) => b.payout - a.payout);
    const adjs = (adjustments || []).filter((a) => a.partner_id === breakdown.partner_id && (!breakdown.period || a.period === breakdown.period));
    const grossTotal = merchants.reduce((s, m) => s + m.gross, 0);
    const payoutTotal = merchants.reduce((s, m) => s + m.payout, 0);
    const adjTotal = adjs.reduce((s, a) => s + (a.amount || 0), 0);
    const summaryMatch = breakdown.period
      ? (data.by_period || []).find((s) => s.partner_id === breakdown.partner_id && s.period === breakdown.period)
      : data.summary.find((s) => s.partner_id === breakdown.partner_id);
    const gst = Number(summaryMatch?.total_gst ?? summaryMatch?.gst ?? 0);
    const tds = Number(summaryMatch?.total_tds ?? summaryMatch?.tds ?? 0);
    const serverNet = summaryMatch?.total_net_payable ?? summaryMatch?.net_payable;
    const netPayable = serverNet != null ? Number(serverNet) : payoutTotal + gst - tds + adjTotal;
    return { merchants, adjs, grossTotal, payoutTotal, adjTotal, summaryMatch, gst, tds, netPayable };
  }, [breakdown, calcRows, adjustments, data.summary, data.by_period]);

  // Merchant tab KPIs — partner-aware (only count THIS partner's slice if filter active).
  const mTotals = useMemo(() => merchantRows.reduce((a, r) => {
    const primary = r.primary_payout || 0;
    const secondary = r.secondary_payout || 0;
    let payoutForRow = primary + secondary;
    if (partnerFilter) {
      payoutForRow = 0;
      if (r.primary_partner_id === partnerFilter) payoutForRow += primary;
      if (r.secondary_partner_id === partnerFilter) payoutForRow += secondary;
    }
    return { payout: a.payout + payoutForRow, vol: a.vol + (r.processed_volume || 0) };
  }, { payout: 0, vol: 0 }), [merchantRows, partnerFilter]);

  // Filtered adjustments for the By Partner tab — sorted period DESC.
  const filteredAdjustments = useMemo(() => {
    let list = adjustments;
    if (partnerTabFilter) list = list.filter((a) => a.partner_id === partnerTabFilter);
    return [...list].sort((a, b) => (b.period || "").localeCompare(a.period || "") || a.partner_id.localeCompare(b.partner_id));
  }, [adjustments, partnerTabFilter]);

  // History tab derived state
  const historyFilteredFys = useMemo(() => [...new Set(history.map((h) => h.fy).filter(Boolean))].sort(), [history]);
  const historyFiltered = useMemo(() => {
    let rows = [...history];
    if (histPartner) rows = rows.filter((r) => r.partner_id === histPartner);
    if (histFy) rows = rows.filter((r) => r.fy === histFy);
    const { key, dir } = histSort;
    rows.sort((a, b) => {
      const va = a[key] ?? ""; const vb = b[key] ?? "";
      if (typeof va === "number" && typeof vb === "number") return dir === "asc" ? va - vb : vb - va;
      return dir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return rows;
  }, [history, histPartner, histFy, histSort]);
  const visibleHistIds = useMemo(() => historyFiltered.map((r) => r.id), [historyFiltered]);
  const allHistSelected = visibleHistIds.length > 0 && visibleHistIds.every((id) => histSelectedIds.has(id));
  const someHistSelected = histSelectedIds.size > 0 && !allHistSelected;
  const histTotals = useMemo(() => historyFiltered.reduce((a, r) => ({
    comm: a.comm + (r.commission || 0), tds: a.tds + (r.tds || 0), gst: a.gst + (r.gst || 0),
    po: a.po + (r.total_po || 0), paid: a.paid + (r.paid ? (r.total_po || r.commission || 0) : 0),
  }), { comm: 0, tds: 0, gst: 0, po: 0, paid: 0 }), [historyFiltered]);

  // ---------- handlers ----------
  const openBreakdown = (partnerId, partnerName, p = null) => {
    setBreakdown({ partner_id: partnerId, partner_name: partnerName, period: p });
  };
  const toggleExpand = (key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // CSV export — per-merchant per-effective-date breakdown (iter31.6).
  const exportEffectiveDateBreakdown = (group) => {
    const headers = [
      "Period", "Merchant ID", "Merchant", "Gateway", "LOB",
      "Effective Date", "Rate Anchor Date", "Volume",
      "Primary Partner ID", "Primary Partner", "Primary Share (bps)", "Primary Payout",
      "Secondary Partner ID", "Secondary Partner", "Secondary Share (bps)", "Secondary Payout",
      "Line Total Payout",
    ];
    const lines = (group.effective_date_lines || []).map((s) => [
      group.period, group.merchant_id, group.merchant_name, group.gateway, group.lob,
      s.effective_date || "", s.rate_anchor_date || "",
      Math.round(s.processed_volume || 0),
      s.primary_partner_id || "", s.primary_partner_name || "",
      Math.round((s.primary_share_value || 0) * 10000),
      Math.round(s.primary_payout || 0),
      s.secondary_partner_id || "", s.secondary_partner_name || "",
      Math.round((s.secondary_share_value || 0) * 10000),
      Math.round(s.secondary_payout || 0),
      Math.round((s.primary_payout || 0) + (s.secondary_payout || 0)),
    ]);
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...lines].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payout-detail_${group.merchant_id}_${group.period}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Adjustments handlers
  const saveAdj = async () => {
    try {
      const payload = { ...adjForm, amount: Number(adjForm.amount || 0) };
      if (adjEditId) await api.put(`/partner-adjustments/${adjEditId}`, payload);
      else await api.post("/partner-adjustments", payload);
      toast.success("Saved"); setAdjOpen(false);
      setAdjForm({ partner_id: "", period: "", label: "", amount: 0, notes: "" }); setAdjEditId(null);
      load(period);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeAdj = async (id) => {
    if (id?.startsWith("virt-")) { toast.info("This is a recurring deduction. Edit or remove it on the Partner's profile."); return; }
    if (!await openConfirm({ title: "Delete adjustment?", message: "This adjustment will be removed from the partner's payout for the period.", confirmLabel: "Delete adjustment", danger: true })) return;
    await api.delete(`/partner-adjustments/${id}`); toast.success("Deleted"); load(period);
  };

  // ====== Per-partner payout lock actions ===================================
  // The lock captures a snapshot of net_payable per (period, partner). Lock
  // does NOT block writes — drift is detected on the read side and flipped
  // to a DRIFT status by the backend. Reconcile = re-snapshot at current calc.
  const [lockOpen, setLockOpen] = useState(null);  // {period, partner_id, partner_name, net_payable} | null
  const [lockNote, setLockNote] = useState("");

  const startLock = (period_, row) => {
    setLockOpen({
      period: period_,
      partner_id: row.partner_id,
      partner_name: row.partner_name,
      net_payable: row.net_payable ?? row.total_net_payable,
    });
    setLockNote("");
  };

  const submitLock = async () => {
    try {
      await api.post("/partner-payout-locks", { period: lockOpen.period, partner_id: lockOpen.partner_id, note: lockNote });
      toast.success(`Locked ${lockOpen.partner_name} for ${lockOpen.period}`);
      setLockOpen(null);
      load(period);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to lock"); }
  };

  const unlockPartner = async (period_, partner_id, partner_name) => {
    if (!await openConfirm({
      title: `Unlock ${partner_name}?`,
      message: `The locked snapshot for ${fmtPeriod(period_)} will be discarded. PDFs generated after this will reflect live calculations again.`,
      confirmLabel: "Unlock", danger: true,
    })) return;
    try {
      await api.delete(`/partner-payout-locks/${period_}/${partner_id}`);
      toast.success("Unlocked");
      load(period);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to unlock"); }
  };

  const reconcilePartner = async (period_, partner_id, partner_name, driftAmount) => {
    const direction = driftAmount > 0 ? "increased" : "decreased";
    if (!await openConfirm({
      title: `Reconcile ${partner_name}`,
      message: `The locked snapshot has drifted by ₹${Math.abs(driftAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (${direction}). Re-lock at the current calculated total?`,
      confirmLabel: "Re-lock at current total",
    })) return;
    try {
      const res = await api.post(`/partner-payout-locks/${period_}/${partner_id}/reconcile`, { period: period_, partner_id });
      toast.success(`Re-locked: ₹${res.data.before.toFixed(2)} → ₹${res.data.after.toFixed(2)}`);
      load(period);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reconcile"); }
  };
  // ====== End lock actions ==================================================

  const editAdj = async (r) => {
    if (r.source === "standing") {
      const current = Math.abs(Number(r.standing_amount || r.amount || 0));
      const raw = await openPrompt({
        title: `Override "${r.label}"`,
        message: `Standing amount for ${fmtPeriod(r.period)} is ₹${current.toLocaleString("en-IN")}.\nEnter the new amount (in ₹, non-negative). Use 0 to skip the deduction this month.`,
        defaultValue: String(current), type: "number", confirmLabel: "Override",
        validate: (v) => {
          const n = Number(v);
          if (isNaN(n) || n < 0) return "Enter a non-negative number";
          return null;
        },
      });
      if (raw == null) return;
      const newAmt = Number(raw);
      api.post("/partner-adjustments/override-standing", {
        partner_id: r.partner_id, period: r.period, standing_id: r.standing_id, amount: newAmt, notes: "",
      }).then(() => { toast.success(`Overridden ${fmtPeriod(r.period)} → ₹${newAmt.toLocaleString("en-IN")}`); load(period); })
        .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
      return;
    }
    setAdjForm({ ...r }); setAdjEditId(r.id); setAdjOpen(true);
  };
  const addAdjFor = (pid) => {
    setAdjForm({ partner_id: pid || "", period: period || "", label: "", amount: 0, notes: "" });
    setAdjEditId(null); setAdjOpen(true);
  };

  // History handlers
  const saveHist = async () => {
    try {
      const p = { ...histForm, commission: Number(histForm.commission || 0), tds: Number(histForm.tds || 0), gst: Number(histForm.gst || 0), total_po: Number(histForm.total_po || 0), paid: !!histForm.paid };
      if (histEditId) await api.put(`/partner-payment-history/${histEditId}`, p);
      else await api.post("/partner-payment-history", p);
      toast.success("Saved"); setHistOpen(false); setHistForm(EMPTY_HIST); setHistEditId(null); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeHist = async (id) => {
    if (!await openConfirm({ title: "Delete payment history row?", message: "This payment-out record will be removed from the partner's history.", confirmLabel: "Delete row", danger: true })) return;
    await api.delete(`/partner-payment-history/${id}`); toast.success("Deleted"); loadHistory();
  };
  const editHist = (r) => { setHistForm({ ...r }); setHistEditId(r.id); setHistOpen(true); };
  const togglePaid = async (row) => {
    try {
      await api.put(`/partner-payment-history/${row.id}`, { ...row, paid: !row.paid });
      toast.success(!row.paid ? "Marked paid" : "Marked unpaid");
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleHistSort = (k) => setHistSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));
  const toggleHistOne = (id) => setHistSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllHistVisible = () => setHistSelectedIds((s) => {
    const n = new Set(s);
    if (allHistSelected) visibleHistIds.forEach((id) => n.delete(id));
    else visibleHistIds.forEach((id) => n.add(id));
    return n;
  });
  const clearHistSelection = () => setHistSelectedIds(new Set());
  const bulkMarkPaid = async (paid) => {
    if (!histSelectedIds.size) return;
    try {
      await Promise.all([...histSelectedIds].map((id) => {
        const row = history.find((r) => r.id === id);
        if (!row) return null;
        return api.put(`/partner-payment-history/${id}`, { ...row, paid });
      }));
      toast.success(`${histSelectedIds.size} row${histSelectedIds.size === 1 ? "" : "s"} marked ${paid ? "paid" : "unpaid"}`);
      clearHistSelection(); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bulkDeleteHist = async () => {
    if (!histSelectedIds.size) return;
    if (!await openConfirm({ title: `Delete ${histSelectedIds.size} rows?`, message: "Selected payment history rows will be permanently removed.", confirmLabel: `Delete ${histSelectedIds.size} rows`, danger: true })) return;
    try {
      await Promise.all([...histSelectedIds].map((id) => api.delete(`/partner-payment-history/${id}`)));
      toast.success(`Deleted ${histSelectedIds.size} rows`);
      clearHistSelection(); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const partnerLabel = (pid) => {
    const p = partners.find((x) => x.partner_id === pid);
    return p ? `${p.partner_id} · ${p.partner_name}` : pid;
  };

  // ---------- UI ----------
  const TabBtn = ({ id, children }) => (
    <button
      data-testid={`tab-${id}`}
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors ${tab === id ? "border-[var(--accent)] text-[var(--ink)]" : "border-transparent text-[var(--muted)] hover:text-[var(--ink-2)]"}`}
    >
      {children}
    </button>
  );

  return (
    <div data-testid="payouts-page">
      <PageHeader
        title="Partner Payouts"
        actions={<>
          <div className="w-44"><Select testid="payouts-period" placeholder="All periods" options={periodOptions(data.available_periods || [])} value={period} onChange={onPeriod} /></div>
          {tab === "partner" ? (
            <Btn variant="accent" testid="payouts-export" onClick={() => window.open(apiUrl("/export/payouts"), "_blank")}><Download size={14} /> Export CSV</Btn>
          ) : (
            <Btn variant="accent" testid="calc-export" onClick={() => window.open(apiUrl("/export/calculations"), "_blank")}><Download size={14} /> Export CSV</Btn>
          )}
        </>}
      />
      <div className="px-10 pt-4 border-b border-[var(--line)] flex gap-1" data-testid="payouts-tabs">
        <TabBtn id="partner">By Partner</TabBtn>
        <TabBtn id="merchant">By Merchant (Line Items)</TabBtn>
        <TabBtn id="history">Partner Payment History</TabBtn>
      </div>

      {tab === "partner" && (
        <PayoutsByPartnerTab
          data={data}
          period={period}
          partners={partners}
          partnerTabFilter={partnerTabFilter} setPartnerTabFilter={setPartnerTabFilter}
          partnerLookup={partnerLookup}
          openBreakdown={openBreakdown}
          filteredAdjustments={filteredAdjustments}
          adjOpen={adjOpen} setAdjOpen={setAdjOpen}
          adjForm={adjForm} setAdjForm={setAdjForm}
          adjEditId={adjEditId}
          saveAdj={saveAdj} removeAdj={removeAdj} editAdj={editAdj} addAdjFor={addAdjFor}
          startLock={startLock} unlockPartner={unlockPartner} reconcilePartner={reconcilePartner}
        />
      )}

      {tab === "merchant" && (
        <PayoutsByMerchantTab
          merchantRows={merchantRows} mTotals={mTotals} period={period}
          partnerOptions={partnerOptions} gatewayOptions={gatewayOptions} lobOptions={lobOptions}
          partnerFilter={partnerFilter} setPartnerFilter={setPartnerFilter}
          gatewayFilter={gatewayFilter} setGatewayFilter={setGatewayFilter}
          lobFilter={lobFilter} setLobFilter={setLobFilter}
          expandedRows={expandedRows} toggleExpand={toggleExpand}
          formatShare={formatShare}
          exportEffectiveDateBreakdown={exportEffectiveDateBreakdown}
        />
      )}

      {tab === "history" && (
        <PayoutsHistoryTab
          partners={partners}
          historyFiltered={historyFiltered}
          historyFilteredFys={historyFilteredFys}
          histTotals={histTotals}
          histPartner={histPartner} setHistPartner={setHistPartner}
          histFy={histFy} setHistFy={setHistFy}
          histSort={histSort} toggleHistSort={toggleHistSort}
          histSelectedIds={histSelectedIds}
          allHistSelected={allHistSelected} someHistSelected={someHistSelected}
          toggleHistOne={toggleHistOne} toggleAllHistVisible={toggleAllHistVisible}
          clearHistSelection={clearHistSelection}
          bulkMarkPaid={bulkMarkPaid} bulkDeleteHist={bulkDeleteHist}
          histOpen={histOpen} setHistOpen={setHistOpen}
          histForm={histForm} setHistForm={setHistForm}
          histEditId={histEditId} setHistEditId={setHistEditId}
          saveHist={saveHist} removeHist={removeHist} editHist={editHist} togglePaid={togglePaid}
          partnerLabel={partnerLabel}
        />
      )}

      <PayoutBreakdownModal
        breakdown={breakdown}
        breakdownData={breakdownData}
        onClose={() => setBreakdown(null)}
      />

      {/* Lock & approve modal — snapshot net_payable becomes the PDF anchor */}
      <Modal
        open={!!lockOpen}
        onClose={() => setLockOpen(null)}
        title={lockOpen ? `Lock payout — ${lockOpen.partner_name} · ${fmtPeriod(lockOpen.period)}` : ""}
        testid="lock-modal"
      >
        {lockOpen && (
          <div className="space-y-4">
            <div className="bg-[var(--surface-2)] border border-[var(--line-2)] rounded-sm px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Snapshot to capture</div>
              <div className="text-[24px] font-bold text-[var(--ink)] num">
                ₹ {fmtINR(lockOpen.net_payable || 0, 2)}
              </div>
              <div className="text-[11.5px] text-[var(--muted)] mt-1">Net payable to partner</div>
            </div>
            <div className="space-y-1.5 text-[12px] text-[var(--ink-2)]">
              <div className="flex gap-2"><Lock size={12} className="mt-0.5 shrink-0 text-[var(--muted)]" /><div>This snapshot is what the PDF will print and what drift detection will compare future calculations against.</div></div>
              <div className="flex gap-2"><Lock size={12} className="mt-0.5 shrink-0 text-[var(--muted)]" /><div>Locking does <b>NOT</b> block volume edits or rate changes. If anything moves the calculation later, the row flips to <b>DRIFT</b> for reconcile.</div></div>
              <div className="flex gap-2"><Lock size={12} className="mt-0.5 shrink-0 text-[var(--muted)]" /><div>Locking does <b>NOT</b> mark the payout as paid. Mark it paid in Partner Payment History after disbursement.</div></div>
            </div>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes (optional)</label>
              <Input
                data-testid="lock-modal-note"
                value={lockNote}
                onChange={(e) => setLockNote(e.target.value)}
                placeholder="e.g. RTGS UTR 2026050800123456"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setLockOpen(null)} testid="lock-modal-cancel">Cancel</Btn>
              <Btn variant="accent" onClick={submitLock} testid="lock-modal-submit">
                <Lock size={13} /> Lock snapshot
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
